import React, { useState, useEffect } from 'react';
import { FaServer, FaDatabase, FaAws, FaNetworkWired, FaSyncAlt, FaCog } from 'react-icons/fa';
import S3BucketAnalytics from './S3BucketAnalytics';
import MongoDBAnalytics from './MongoDBAnalytics';
import PostgreSQLAnalytics from './PostgreSQLAnalytics';
import ConfigureCredentials from './ConfigureCredentials';
import DatabaseModal from './DatabaseModal';
import serverAnalyticsService from '../../../services/ServerAnalyticsService';
import mongoDBAnalyticsService from '../../../services/MongoDBAnalyticsService';
import s3AnalyticsService from '../../../services/S3AnalyticsService';
import postgreSQLAnalyticsService from '../../../services/PostgreSQLAnalyticsService';
import { LoadingSpinner } from '../../UI';
import '../../../styles/ServerAnalytics.css';

const ServerAnalyticsDashboard = () => {
  const [data, setData] = useState({
    s3: null,
    mongodb: null,
    postgresql: null,
    systemResources: {
      cpuUsage: 45,
      memoryUsage: 60,
      diskUsage: 48
    },
    recentEvents: [
      { timestamp: '02:45:12', service: 'MongoDB', type: 'error', message: 'Connection failed: timeout' },
      { timestamp: '02:44:30', service: 'S3', type: 'error', message: 'Authentication error' },
      { timestamp: '02:42:15', service: 'System', type: 'warning', message: 'Memory usage above 60%' },
      { timestamp: '02:40:03', service: 'PostgreSQL', type: 'info', message: 'Connected successfully' },
      { timestamp: '02:39:47', service: 'API', type: 'success', message: 'Server started' }
    ]
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [configMode, setConfigMode] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [mongoDBStatus, setMongoDBStatus] = useState({
    status: 'disconnected',
    connectionPercentage: 0
  });
  const [s3Status, setS3Status] = useState({
    status: 'disconnected',
    connectionPercentage: 0
  });
  const [postgresqlData, setPostgresqlData] = useState({
    status: 'loading',
    connectionPercentage: 0,
    tables: 0,
    size: '0 MB',
    sizeBytes: 0
  });

  useEffect(() => {
    fetchData();
    checkMongoDBConnection();
    checkS3Connection();
    checkPostgreSQLConnection();
    
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    const mongoDBInterval = setInterval(() => {
      checkMongoDBConnection();
    }, 30000);
    
    const s3Interval = setInterval(() => {
      checkS3Connection();
    }, 30000);
    
    const postgresInterval = setInterval(() => {
      checkPostgreSQLConnection();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      clearInterval(mongoDBInterval);
      clearInterval(s3Interval);
      clearInterval(postgresInterval);
    };
  }, []);

  const checkMongoDBConnection = async () => {
    try {
      console.log("Checking MongoDB connection...");
      const mongoData = await mongoDBAnalyticsService.getMongoDBAnalytics();
      console.log("MongoDB connection data:", mongoData);
      
      // Update MongoDB status
      if (mongoData && mongoData.connectionState) {
        const status = mongoData.connectionState.status;
        setMongoDBStatus({
          status: status,
          connectionPercentage: status === 'connected' ? 100 : 0,
          error: mongoData.connectionState.error
        });
        
        // Update data state with MongoDB info
        setData(prevData => ({
          ...prevData,
          mongodb: {
            isConnected: status === 'connected',
            collections: mongoData.clusters?.[0]?.collectionCount || 0,
            documents: mongoData.clusters?.[0]?.documentCount || 0,
            ...mongoData
          }
        }));
        
        // Add connection event to recent events
        if (status === 'connected') {
          addEvent({
            timestamp: formatTime(new Date()),
            service: 'MongoDB',
            type: 'success',
            message: 'Connected successfully'
          });
        } else if (status === 'error' || status === 'disconnected') {
          addEvent({
            timestamp: formatTime(new Date()),
            service: 'MongoDB',
            type: 'error',
            message: `Connection failed: ${mongoData.connectionState.error || 'Unknown error'}`
          });
        }
      }
    } catch (error) {
      console.error("Error checking MongoDB connection:", error);
      setMongoDBStatus({
        status: 'error',
        connectionPercentage: 0,
        error: error.message
      });
      
      addEvent({
        timestamp: formatTime(new Date()),
        service: 'MongoDB',
        type: 'error',
        message: `Connection error: ${error.message}`
      });
    }
  };

  const checkS3Connection = async () => {
    try {
      console.log("Checking S3 connection...");
      const s3Data = await s3AnalyticsService.getS3Analytics();
      console.log("S3 connection data:", s3Data);
      
      // Update S3 status
      if (s3Data && s3Data.connectionState) {
        const status = s3Data.connectionState.status;
        setS3Status({
          status: status,
          connectionPercentage: status === 'connected' ? 100 : 0,
          error: s3Data.connectionState.error
        });
        
        // Update data state with S3 info
        setData(prevData => ({
          ...prevData,
          s3: {
            isConnected: status === 'connected',
            buckets: s3Data.totalBuckets || 0,
            totalSize: s3Data.totalSize || '0 MB',
            totalObjects: s3Data.totalObjects || 0,
            buckets: s3Data.buckets || [],
            ...s3Data
          }
        }));
        
        // Add connection event to recent events
        if (status === 'connected') {
          addEvent({
            timestamp: formatTime(new Date()),
            service: 'S3',
            type: 'success',
            message: 'Connected successfully'
          });
        } else if (status === 'error' || status === 'disconnected') {
          addEvent({
            timestamp: formatTime(new Date()),
            service: 'S3',
            type: 'error',
            message: `Connection failed: ${s3Data.connectionState.error || 'Unknown error'}`
          });
        }
      }
    } catch (error) {
      console.error("Error checking S3 connection:", error);
      setS3Status({
        status: 'error',
        connectionPercentage: 0,
        error: error.message
      });
      
      addEvent({
        timestamp: formatTime(new Date()),
        service: 'S3',
        type: 'error',
        message: `Connection error: ${error.message}`
      });
    }
  };

  const checkPostgreSQLConnection = async () => {
    try {
      console.log("Checking PostgreSQL connection...");
      const postgresData = await postgreSQLAnalyticsService.getPostgreSQLAnalytics();
      console.log("PostgreSQL connection data:", postgresData);
      
      if (postgresData && postgresData.status === 'connected') {
        // Calculate totals across all databases
        let totalTables = 0;
        let totalSizeBytes = 0;
        let totalActiveConnections = 0;
        
        if (postgresData.databases && postgresData.databases.length > 0) {
          postgresData.databases.forEach(db => {
            totalTables += db.tableCount || 0;
            totalSizeBytes += db.sizeBytes || 0;
            totalActiveConnections += db.activeConnections || 0;
          });
        }
        
        // Format total size
        let totalSize;
        if (totalSizeBytes >= 1024 * 1024 * 1024) {
          totalSize = `${(totalSizeBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
        } else if (totalSizeBytes >= 1024 * 1024) {
          totalSize = `${(totalSizeBytes / (1024 * 1024)).toFixed(2)} MB`;
        } else if (totalSizeBytes >= 1024) {
          totalSize = `${(totalSizeBytes / 1024).toFixed(2)} KB`;
        } else {
          totalSize = `${totalSizeBytes} B`;
        }
        
        setPostgresqlData({
          status: 'connected',
          connectionPercentage: 100,
          tables: totalTables,
          size: totalSize,
          sizeBytes: totalSizeBytes,
          activeConnections: totalActiveConnections,
          version: postgresData.version || 'Unknown',
          databases: postgresData.databases || []
        });
        
        // Add connection event to recent events
        addEvent({
          timestamp: formatTime(new Date()),
          service: 'PostgreSQL',
          type: 'success',
          message: 'Connected successfully'
        });
      } else {
        setPostgresqlData({
          status: 'error',
          connectionPercentage: 0,
          tables: 0,
          size: '0 MB',
          sizeBytes: 0,
          version: 'Unknown',
          databases: []
        });
        
        addEvent({
          timestamp: formatTime(new Date()),
          service: 'PostgreSQL',
          type: 'error',
          message: `Connection failed: ${postgresData?.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      console.error("Error checking PostgreSQL connection:", error);
      setPostgresqlData({
        status: 'error',
        connectionPercentage: 0,
        tables: 0,
        size: '0 MB',
        sizeBytes: 0,
        version: 'Unknown',
        databases: []
      });
      
      addEvent({
        timestamp: formatTime(new Date()),
        service: 'PostgreSQL',
        type: 'error',
        message: `Connection error: ${error.message}`
      });
    }
  };

  const addEvent = (event) => {
    setData(prevData => ({
      ...prevData,
      recentEvents: [event, ...prevData.recentEvents].slice(0, 10)
    }));
  };

  const fetchData = async () => {
    setRefreshing(true);
    try {
      // Fetch existing data
      // ... existing fetch calls ...
      
      // Also fetch PostgreSQL data
      await checkPostgreSQLConnection();
      
      // ... rest of existing code ...
    } catch (error) {
      // ... existing error handling ...
    } finally {
      setRefreshing(false);
    }
  };

  const openModal = (database) => {
    setSelectedDatabase(database);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDatabase(null);
  };

  const toggleConfigMode = () => {
    setConfigMode(!configMode);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatUptime = (uptime) => {
    if (!uptime) return 'N/A';
    return `${uptime}%`;
  };

  const formatSize = (size) => {
    if (!size && size !== 0) return 'N/A';
    if (size < 1024) return `${size} MB`;
    return `${(size / 1024).toFixed(2)} GB`;
  };

  // Generate LED bars for resource visualization
  const generateLEDs = (percentage, count = 10) => {
    const activeLEDs = Math.floor((percentage / 100) * count);
    return Array(count).fill().map((_, i) => {
      let ledClass = 'led';
      if (i < activeLEDs) {
        if (percentage > 80) ledClass += ' active red';
        else if (percentage > 50) ledClass += ' active blue';
        else ledClass += ' active green';
      }
      return <div key={i} className={ledClass}></div>;
    });
  };

  // Calculate system health score
  const calculateHealthScore = () => {
    let score = 100;
    if (!data.s3?.isConnected) score -= 25;
    if (!data.mongodb?.isConnected) score -= 25;
    if (data.systemResources.cpuUsage > 80) score -= 15;
    if (data.systemResources.memoryUsage > 80) score -= 15;
    if (data.systemResources.diskUsage > 80) score -= 15;
    return Math.max(0, score);
  };

  const healthScore = calculateHealthScore();
  const healthStatus = healthScore > 80 ? 'Excellent' : 
                      healthScore > 60 ? 'Good' : 
                      healthScore > 40 ? 'Fair' : 
                      healthScore > 20 ? 'Poor' : 'Critical';

  return (
    <div className="server-analytics-dashboard">
      <div className="analytics-header">
        <div className="header-left">
          <h2>
            <FaServer className="header-icon" />
            System Command Center
          </h2>
          <div className="header-controls">
            <button className="control-button" onClick={fetchData}>
              <FaSyncAlt className={refreshing ? 'spin' : ''} />
              <span>Refresh</span>
            </button>
            <button className="control-button" onClick={toggleConfigMode}>
              <FaCog />
              <span>Configure</span>
            </button>
          </div>
        </div>
        <div className="system-time">
          <span>{formatTime(currentTime).split(':')[0]}</span>
          <span className="time-separator">:</span>
          <span>{formatTime(currentTime).split(':')[1]}</span>
          <span className="time-separator">:</span>
          <span>{formatTime(currentTime).split(':')[2]}</span>
        </div>
      </div>

      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Initializing system diagnostics...</p>
        </div>
      )}

      {!loading && configMode && (
        <ConfigureCredentials onClose={toggleConfigMode} />
      )}

      {!loading && !configMode && (
        <>
          {errors.general && (
            <div className="error-message">{errors.general}</div>
          )}

          <div className="dashboard-grid">
            <div className="system-status-panel">
              <div className="panel-header">
                <h3><FaNetworkWired /> System Status</h3>
              </div>
              <div className="system-health-meter">
                <div className="health-gauge">
                  <svg viewBox="0 0 120 120" className="gauge">
                    <circle cx="60" cy="60" r="56" className="gauge-background" />
                    <circle 
                      cx="60" 
                      cy="60" 
                      r="56" 
                      className={`gauge-value ${
                        healthScore > 80 ? 'excellent' : 
                        healthScore > 60 ? 'good' : 
                        healthScore > 40 ? 'fair' : 
                        'poor'
                      }`} 
                      style={{ 
                        strokeDasharray: `${healthScore * 3.51}, 351` 
                      }} 
                    />
                    <text x="60" y="60" className="gauge-percentage">{healthScore}%</text>
                    <text x="60" y="75" className="gauge-label">{healthStatus}</text>
                  </svg>
                </div>
                <div className="health-issues">
                  {data.s3?.isConnected === false && (
                    <div className="issue-item">
                      <span className="issue-icon">⚠️</span>
                      <span>S3 connection failed</span>
                    </div>
                  )}
                  {data.mongodb?.isConnected === false && (
                    <div className="issue-item">
                      <span className="issue-icon">⚠️</span>
                      <span>MongoDB connection failed</span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="resource-monitors">
                <div className="resource-monitor">
                  <div className="resource-header">
                    <span>CPU</span>
                    <span className={`resource-value ${
                      data.systemResources.cpuUsage > 80 ? 'critical' : 
                      data.systemResources.cpuUsage > 60 ? 'warning' : 'normal'
                    }`}>{data.systemResources.cpuUsage}%</span>
                  </div>
                  <div className="led-bar">
                    {generateLEDs(data.systemResources.cpuUsage)}
                  </div>
                </div>
                
                <div className="resource-monitor">
                  <div className="resource-header">
                    <span>Memory</span>
                    <span className={`resource-value ${
                      data.systemResources.memoryUsage > 80 ? 'critical' : 
                      data.systemResources.memoryUsage > 60 ? 'warning' : 'normal'
                    }`}>{data.systemResources.memoryUsage}%</span>
                  </div>
                  <div className="led-bar">
                    {generateLEDs(data.systemResources.memoryUsage)}
                  </div>
                </div>
                
                <div className="resource-monitor">
                  <div className="resource-header">
                    <span>Disk</span>
                    <span className={`resource-value ${
                      data.systemResources.diskUsage > 80 ? 'critical' : 
                      data.systemResources.diskUsage > 60 ? 'warning' : 'normal'
                    }`}>{data.systemResources.diskUsage}%</span>
                  </div>
                  <div className="led-bar">
                    {generateLEDs(data.systemResources.diskUsage)}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="databases-panel">
              <div className="panel-header">
                <h3><FaDatabase /> Database Services</h3>
              </div>
              <div className="database-cards">
                <div className={`database-card ${data.s3?.isConnected ? 'online' : 'error'}`} onClick={() => openModal('s3')}>
                  <div className={`status-indicator ${s3Status.status === 'connected' ? 'online' : 'offline'}`}></div>
                  <div className="database-card-header">
                    <div className="database-icon s3">
                      <FaAws />
                    </div>
                    <div className="database-info">
                      <h4>S3 Storage</h4>
                    </div>
                  </div>
                  
                  <div className="database-metrics">
                    <div className="metric">
                      <div className="metric-header">
                        <span className="metric-label">Buckets</span>
                        <span className="metric-value">{data.s3?.buckets?.length || 0}</span>
                      </div>
                      <div className="gauge-bar">
                        <div 
                          className="gauge-fill low" 
                          style={{ width: `${Math.min(100, ((data.s3?.buckets?.length || 0) / 10) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="metric">
                      <div className="metric-header">
                        <span className="metric-label">Storage</span>
                        <span className="metric-value">{data.s3?.totalSize || '0 MB'}</span>
                      </div>
                      <div className="gauge-bar">
                        <div 
                          className={`gauge-fill ${
                            data.s3?.totalSizeBytes > 800000000 ? 'critical' : 
                            data.s3?.totalSizeBytes > 500000000 ? 'high' : 
                            data.s3?.totalSizeBytes > 200000000 ? 'medium' : 'low'
                          }`}
                          style={{ width: `${Math.min(100, ((data.s3?.totalSizeBytes || 0) / 1024000000) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="uptime-indicator">
                      <svg viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="16" className="uptime-circle" />
                        <circle 
                          cx="18" 
                          cy="18" 
                          r="16" 
                          className={`uptime-value ${s3Status.status === 'connected' ? 'excellent' : 'poor'}`} 
                          style={{ 
                            strokeDasharray: `${s3Status.connectionPercentage}, 100` 
                          }} 
                        />
                        <text x="18" y="18" className="uptime-percentage">{s3Status.connectionPercentage}%</text>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="connection-status">
                    <span className={`status-text ${s3Status.status === 'connected' ? 'online' : 'offline'}`}>
                      {s3Status.status === 'connected' ? 'Connected' : 'Disconnected'}
                      <div className="status-dots">
                        <span className="status-dot"></span>
                        <span className="status-dot"></span>
                        <span className="status-dot"></span>
                      </div>
                    </span>
                  </div>
                </div>
                
                <div className={`database-card ${data.mongodb?.isConnected ? 'online' : 'error'}`} onClick={() => openModal('mongodb')}>
                  <div className={`status-indicator ${mongoDBStatus.status === 'connected' ? 'online' : 'offline'}`}></div>
                  <div className="database-card-header">
                    <div className="database-icon mongodb">
                      <FaDatabase />
                    </div>
                    <div className="database-info">
                      <h4>MongoDB</h4>
                    </div>
                  </div>
                  
                  <div className="database-metrics">
                    <div className="metric">
                      <div className="metric-header">
                        <span className="metric-label">Collections</span>
                        <span className="metric-value">{data.mongodb?.collections || 0}</span>
                      </div>
                    </div>
                    
                    <div className="metric">
                      <div className="metric-header">
                        <span className="metric-label">Documents</span>
                        <span className="metric-value">{data.mongodb?.documents || 0}</span>
                      </div>
                    </div>
                    
                    <div className="uptime-indicator">
                      <svg viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="16" className="uptime-circle" />
                        <circle 
                          cx="18" 
                          cy="18" 
                          r="16" 
                          className={`uptime-value ${mongoDBStatus.status === 'connected' ? 'excellent' : 'critical'}`}
                          style={{ 
                            strokeDasharray: `${mongoDBStatus.connectionPercentage}, 100` 
                          }} 
                        />
                        <text x="18" y="18" className="uptime-percentage">{mongoDBStatus.connectionPercentage}%</text>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="connection-status">
                    <span className={`status-text ${mongoDBStatus.status === 'connected' ? 'online' : 'offline'}`}>
                      {mongoDBStatus.status === 'connected' ? 'Connected' : 'Disconnected'}
                      <div className="status-dots">
                        <span className="status-dot"></span>
                        <span className="status-dot"></span>
                        <span className="status-dot"></span>
                      </div>
                    </span>
                  </div>
                </div>
                
                <div className={`database-card ${postgresqlData.status === 'connected' ? 'online' : 'error'}`} onClick={() => openModal('postgresql')}>
                  <div className={`status-indicator ${postgresqlData.status === 'connected' ? 'online' : 'offline'}`}></div>
                  <div className="database-card-header">
                    <div className="database-icon postgresql">
                      <FaDatabase />
                    </div>
                    <div className="database-info">
                      <h4>PostgreSQL</h4>
                    </div>
                  </div>
                  
                  <div className="database-metrics">
                    <div className="metric">
                      <div className="metric-header">
                        <span className="metric-label">Tables</span>
                        <span className="metric-value">{postgresqlData.tables}</span>
                      </div>
                      <div className="gauge-bar">
                        <div 
                          className="gauge-fill medium" 
                          style={{ width: `${Math.min(100, (postgresqlData.tables / 50) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="metric">
                      <div className="metric-header">
                        <span className="metric-label">Size</span>
                        <span className="metric-value">{postgresqlData.size}</span>
                      </div>
                      <div className="gauge-bar">
                        <div 
                          className={`gauge-fill ${
                            postgresqlData.sizeBytes > 800000000 ? 'critical' : 
                            postgresqlData.sizeBytes > 500000000 ? 'high' : 
                            postgresqlData.sizeBytes > 200000000 ? 'medium' : 'low'
                          }`}
                          style={{ width: `${Math.min(100, (postgresqlData.sizeBytes / 1024000000) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="uptime-indicator">
                      <svg viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="16" className="uptime-circle" />
                        <circle 
                          cx="18" 
                          cy="18" 
                          r="16" 
                          className={`uptime-value ${postgresqlData.status === 'connected' ? 'excellent' : 'poor'}`} 
                          style={{ 
                            strokeDasharray: `${postgresqlData.connectionPercentage}, 100` 
                          }} 
                        />
                        <text x="18" y="18" className="uptime-percentage">{postgresqlData.connectionPercentage}%</text>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="connection-status">
                    <span className={`status-text ${postgresqlData.status === 'connected' ? 'online' : 'offline'}`}>
                      {postgresqlData.status === 'connected' ? 'Connected' : 'Disconnected'}
                      <div className="status-dots">
                        <span className="status-dot"></span>
                        <span className="status-dot"></span>
                        <span className="status-dot"></span>
                      </div>
                    </span>
                  </div>
                </div>
                
                <div className="database-card online" onClick={() => openModal('api')}>
                  <div className="status-indicator"></div>
                  <div className="database-card-header">
                    <div className="database-icon api">
                      <FaServer />
                    </div>
                    <div className="database-info">
                      <h4>API Server</h4>
                    </div>
                  </div>
                  
                  <div className="database-metrics">
                    <div className="metric">
                      <div className="metric-header">
                        <span className="metric-label">Uptime</span>
                        <span className="metric-value">99.8%</span>
                      </div>
                      <div className="gauge-bar">
                        <div 
                          className="gauge-fill excellent" 
                          style={{ width: '99.8%' }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="metric">
                      <div className="metric-header">
                        <span className="metric-label">Response</span>
                        <span className="metric-value">124ms</span>
                      </div>
                      <div className="gauge-bar">
                        <div 
                          className="gauge-fill low"
                          style={{ width: '12.4%' }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="uptime-indicator">
                      <svg viewBox="0 0 36 36">
                        <circle cx="18" cy="18" r="16" className="uptime-circle" />
                        <circle 
                          cx="18" 
                          cy="18" 
                          r="16" 
                          className="uptime-value excellent" 
                          style={{ 
                            strokeDasharray: "99.8, 100" 
                          }} 
                        />
                        <text x="18" y="18" className="uptime-percentage">99.8%</text>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="connection-status">
                    <span className="status-text online">
                      Connected
                      <div className="status-dots">
                        <span className="status-dot"></span>
                        <span className="status-dot"></span>
                        <span className="status-dot"></span>
                      </div>
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="events-console">
              <div className="console-header">
                <h3>System Events</h3>
                <div className="console-controls">
                  <button className="console-button" onClick={fetchData}>
                    <FaSyncAlt className={refreshing ? 'spin' : ''} />
                    Refresh
                  </button>
                </div>
              </div>
              <div className="console-content">
                {data.recentEvents && data.recentEvents.length > 0 ? (
                  data.recentEvents.map((event, index) => (
                    <div key={index} className={`console-line ${event.type}`}>
                      <span className="console-timestamp">[{event.timestamp}]</span>
                      <span className="console-service">[{event.service}]</span>
                      <span className="console-message">{event.message}</span>
                    </div>
                  ))
                ) : (
                  <div className="console-line">
                    <span className="console-timestamp">[{formatTime(currentTime)}]</span>
                    <span className="console-service">[System]</span>
                    <span className="console-message">No recent events to display</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Database Modal */}
          {modalOpen && selectedDatabase && (
            <DatabaseModal 
              database={selectedDatabase}
              data={data[selectedDatabase]}
              onClose={closeModal}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ServerAnalyticsDashboard; 